import type { TamCalculatorFilters } from "~/react/types/tam_calculator"

/**
 * Builds URLSearchParams from TamCalculatorFilters object.
 * Handles arrays, objects (as key lists), and primitive values.
 */
export function buildTamFilterParams(filters: TamCalculatorFilters): URLSearchParams {
  const params = new URLSearchParams()

  Object.entries(filters).forEach(([key, value]) => {
    if (value === undefined || value === null) return

    if (Array.isArray(value)) {
      if (value.length === 0) return
      value.forEach((v) => {
        const strValue = Array.isArray(v) ? String(v[0]) : String(v)
        params.append(`${key}[]`, strValue)
      })
    } else if (typeof value === "object") {
      const keys = Object.keys(value)
      if (keys.length === 0) return
      keys.forEach((k) => params.append(`${key}[]`, k))
    } else {
      params.append(key, String(value))
    }
  })

  return params
}

/**
 * Checks if the filters object has any actual filter values set (excluding ACV).
 * We need at least one company filter to perform a search.
 */
export function hasActiveTamFilters(filters: TamCalculatorFilters): boolean {
  return Object.keys(filters).some((key) => {
    // ACV alone doesn't constitute "active filters" - we need company filters
    if (key === "acv") return false

    const value = filters[key as keyof TamCalculatorFilters]
    if (value === undefined || value === null) return false
    if (Array.isArray(value) && value.length === 0) return false
    if (typeof value === "object" && Object.keys(value).length === 0) return false
    return true
  })
}

/**
 * Calculate TAM (Total Addressable Market) client-side.
 * TAM = ACV (Average Contract Value) × Number of Companies
 */
export function calculateTam(acv: number | null, numberOfCompanies: number): number | null {
  if (!acv || acv <= 0 || !numberOfCompanies || numberOfCompanies <= 0) {
    return null
  }
  return acv * numberOfCompanies
}

